/**
 * Générateur — Attendu 961 — Démontrer qu'une médiane partage un triangle en
 * deux triangles d'aires égales.
 *
 * Niveau : 5ème | Thème #10 Triangles (O20) | Domaine : Espace et géométrie
 * Source prof : cluster 5, Ex.10 (la démonstration) + Ex.11 (application),
 * extraction exerciseur/triangles.
 *
 * 3 formats / 2 cartes :
 *  1. demo        — Ex.10 : CLASSER « Ça démontre / Ça ne démontre pas » + QCM
 *                   justification (A₂ = A₃ car I milieu → même hauteur, bases
 *                   égales). Réinvestit « même base + même hauteur → même aire ».
 *  2. application — Ex.11 : l'aire du triangle → la moitié par la médiane
 *                   (÷ 2 SANS aucune longueur) + QCM justification.
 */

import {
    randInt, randomChoice, shuffle, pickLettresSains,
    makeSaisie, makeQcm, makeClasser,
} from '../../../utils';

const ATTENDU_ID = 961;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DEMO = 'La démonstration (médiane → aires égales)';
const L_APP = 'Application : la moitié de l\'aire';

const pick = (n) => pickLettresSains('ABCDEFGHJKLMNPRSTUVWXYZ'.split(''), n);
const frNb = (x) => {
    const s = x < 0 ? '-' : '';
    const str = Math.abs(x).toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
    return s + str.replace('.', ',');
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — La démonstration (Ex.10)
// ═══════════════════════════════════════════════════════════

// Refonte bilan prof D6 (2026-07-27) : l'ancien relier A₁/A₂/A₃ ↔ aires était
// indécidable (I milieu ⟹ IN = IP, donc A₂ et A₃ sont numériquement égales —
// l'appariement croisé était défendable, un élève juste pouvait être compté
// faux). Refonte en CLASSER « Ça démontre / Ça ne démontre pas » : le cœur de
// l'attendu est la NATURE d'une démonstration. Règle d'homogénéité : la
// colonne « ne démontre pas » ne contient que des démarches INSUFFISANTES,
// jamais d'énoncés faux (« ont l'air superposables », pas « sont
// superposables »). Pool validé : 2 formulations par famille.

// « Ça démontre » — F1 générique, F2 instanciée avec les lettres du tirage.
const F1_DEMONTRE = [
    'Les deux triangles ont des bases de même longueur et la même hauteur.',
    'Les deux triangles ont la même hauteur, et leurs bases sont égales : base × hauteur ÷ 2 donne le même résultat.',
];
const F2_DEMONTRE = [
    (S, B1, B2, M) => `${B1}${M} = ${M}${B2} car ${M} est le milieu de [${B1}${B2}], et la hauteur issue de ${S} est commune aux deux triangles.`,
    (S, B1, B2, M) => `Les deux triangles partagent la hauteur issue de ${S}, et leurs bases ${B1}${M} et ${M}${B2} sont égales.`,
];
// « Ça ne démontre pas » — 6 familles, 2 formulations chacune.
const FAMILLES_NON = [
    ['mesure', [
        'J\'ai mesuré les deux aires sur la figure, je trouve la même.',
        'J\'ai compté les carreaux sous chaque triangle : c\'est pareil.',
    ]],
    ['logiciel', [
        'Je l\'ai vérifié sur GeoGebra en déplaçant les points.',
        'Le logiciel affiche la même aire pour les deux triangles, même quand je bouge les sommets.',
    ]],
    ['exemples', [
        'Ça marche sur les trois triangles que j\'ai testés.',
        'J\'ai essayé avec plusieurs triangles différents, ça marche à chaque fois.',
    ]],
    ['cas_particulier', [
        'C\'est vrai pour un triangle équilatéral, donc c\'est vrai pour tous les triangles.',
        'Je l\'ai vérifié sur un triangle rectangle, donc la propriété est démontrée.',
    ]],
    ['apparence', [
        'Les deux triangles ont l\'air superposables sur la figure.',
        'Ça se voit sur le dessin que les deux aires sont égales.',
    ]],
    ['autorite', [
        'C\'est écrit dans le manuel, donc c\'est vrai.',
        'Le professeur nous a dit que c\'était vrai.',
    ]],
];

const genDemoClasser = () => {
    const [nS, nB1, nB2, nM] = pick(4);     // triangle S B1 B2, M milieu de [B1 B2]
    const cite = (s) => `« ${s} »`;

    const demontre = [
        cite(randomChoice(F1_DEMONTRE)),
        cite(randomChoice(F2_DEMONTRE)(nS, nB1, nB2, nM)),
    ];
    const famillesTirees = shuffle([...FAMILLES_NON]).slice(0, 4);
    const neDemontrePas = famillesTirees.map(([, formulations]) => cite(randomChoice(formulations)));

    const texte = `<p>Dans le triangle ${nS}${nB1}${nB2}, ${nM} est le milieu de [${nB1}${nB2}]. `
        + `On veut prouver que les triangles ${nS}${nB1}${nM} et ${nS}${nM}${nB2} ont la même aire. `
        + 'Des élèves de la classe proposent chacun leur argument. Classe chaque argument dans la bonne colonne.</p>';

    const payload = makeClasser([
        { name: 'Ça démontre', items: demontre },
        { name: 'Ça ne démontre pas', items: neDemontrePas },
    ], texte, { ...opts(L_DEMO) });
    payload._meta.generatorKey = 'demo_classer';
    payload._meta.params = { familles: famillesTirees.map(([code]) => code) };
    return payload;
};

const genDemoQcm = () => {
    const [nM, nN, nP, nI] = pick(4);
    const correct = `Ils ont la même hauteur (issue de ${nM}) et des bases égales : `
        + `${nI}${nN} = ${nI}${nP} car ${nI} est le milieu de [${nN}${nP}].`;
    const distract = [
        `Parce que ${nM}${nN} = ${nM}${nP}.`,
        'Parce qu\'ils ont exactement la même forme.',
        `Parce que l'angle en ${nM} est partagé en deux angles égaux.`,
    ];
    const texte = `<p>Dans le triangle ${nM}${nN}${nP}, ${nI} est le milieu de [${nN}${nP}] et la `
        + `hauteur issue de ${nM} a pour pied H. Pourquoi les triangles ${nM}${nN}${nI} et `
        + `${nM}${nI}${nP} ont-ils la même aire ?</p>`;
    const payload = makeQcm([correct, ...distract], correct, texte, { ...opts(L_DEMO) });
    payload._meta.generatorKey = 'demo_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Application (Ex.11)
// ═══════════════════════════════════════════════════════════

const genApplicationTexte = () => {
    const [nS, nO, nU, nR] = pick(4);       // triangle nS nO nU, R milieu de [nS nU]
    const St = randInt(40, 120) / 10;        // aire en cm² (1 décimale)
    const moit = St / 2;
    const texte = `<p>L'aire du triangle ${nS}${nO}${nU} vaut ${frNb(St)} cm². ${nR} est le milieu `
        + `de [${nS}${nU}], donc (${nO}${nR}) est la médiane issue de ${nO}. `
        + `Quelle est l'aire du triangle ${nO}${nR}${nS} ?</p>`;
    const payload = makeSaisie(frNb(moit), texte, {
        ...opts(L_APP), strict: true, suffixe: 'cm²',
    });
    payload._meta.generatorKey = 'application_texte';
    return payload;
};

const genApplicationQcm = () => {
    const [nS, nO, nU, nR] = pick(4);
    const correct = `Parce que (${nO}${nR}) est une médiane : elle partage le triangle en deux triangles de même aire.`;
    const distract = [
        `Parce que (${nO}${nR}) est une hauteur du triangle.`,
        `Parce que ${nO}${nS} = ${nO}${nU}.`,
        'Parce que le triangle est isocèle.',
    ];
    const texte = `<p>Dans le triangle ${nS}${nO}${nU}, ${nR} est le milieu de [${nS}${nU}]. `
        + `Pourquoi l'aire du triangle ${nO}${nR}${nS} est-elle la moitié de celle de ${nS}${nO}${nU} ?</p>`;
    const payload = makeQcm([correct, ...distract], correct, texte, { ...opts(L_APP) });
    payload._meta.generatorKey = 'application_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    demo_classer: genDemoClasser,
    demo_qcm_unique: genDemoQcm,
    application_texte: genApplicationTexte,
    application_qcm_unique: genApplicationQcm,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', texte: 'Saisie', classer: 'Classer' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'demo', label: L_DEMO, formatsList: buildFormats('demo') },
    { id: 'application', label: L_APP, formatsList: buildFormats('application') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genDemoClasser, genDemoQcm, genApplicationTexte, genApplicationQcm,
};
